import {
  toContactBulkMatchingQuery,
  type ContactBulkSelection,
  type ContactListQuery,
} from "@multi-chat/shared";

export type ContactsSelectionMode = "ids" | "matching_query" | "all_active";

export type ContactsHeaderCheckboxState = "none" | "some" | "all";

export type ContactsSelectionState =
  | { mode: "ids"; ids: ReadonlySet<number> }
  | { mode: "matching_query"; queryKey: string; excludedIds: ReadonlySet<number> }
  | { mode: "all_active"; excludedIds: ReadonlySet<number> };

function emptyExcludedIds(): ReadonlySet<number> {
  return new Set();
}

function cloneExcludedIds(ids: Iterable<number>): Set<number> {
  return new Set(ids);
}

function withSemanticExclusions(
  selection: Extract<ContactsSelectionState, { mode: "matching_query" | "all_active" }>,
  excludedIds: ReadonlySet<number>,
): ContactsSelectionState {
  if (selection.mode === "matching_query") {
    return { mode: "matching_query", queryKey: selection.queryKey, excludedIds };
  }
  return { mode: "all_active", excludedIds };
}

export function emptyContactsSelection(): ContactsSelectionState {
  return { mode: "ids", ids: new Set() };
}

export function matchingQuerySelection(queryKey: string): ContactsSelectionState {
  return { mode: "matching_query", queryKey, excludedIds: emptyExcludedIds() };
}

export function allActiveSelection(): ContactsSelectionState {
  return { mode: "all_active", excludedIds: emptyExcludedIds() };
}

export function contactsExcludedIds(selection: ContactsSelectionState): ReadonlySet<number> {
  if (selection.mode === "ids") return emptyExcludedIds();
  return selection.excludedIds;
}

export function contactsSelectionAfterQueryScopeChange(
  selection: ContactsSelectionState,
): ContactsSelectionState {
  if (selection.mode === "all_active") return selection;
  return emptyContactsSelection();
}

export function contactsSelectionAfterPageChange(
  selection: ContactsSelectionState,
): ContactsSelectionState {
  if (selection.mode === "ids") return emptyContactsSelection();
  return selection;
}

export function isContactsMatchingSelectionCurrent(
  selection: ContactsSelectionState,
  currentMatchingQueryKey: string,
): boolean {
  return selection.mode === "matching_query" && selection.queryKey === currentMatchingQueryKey;
}

export function isContactRowSelected(
  selection: ContactsSelectionState,
  contactId: number,
  currentMatchingQueryKey: string,
): boolean {
  switch (selection.mode) {
    case "all_active":
      return !selection.excludedIds.has(contactId);
    case "matching_query":
      return (
        selection.queryKey === currentMatchingQueryKey && !selection.excludedIds.has(contactId)
      );
    case "ids":
      return selection.ids.has(contactId);
    default: {
      const exhaustive: never = selection;
      return exhaustive;
    }
  }
}

export function contactsSelectedCount(
  selection: ContactsSelectionState,
  matchingTotal: number,
  orgActiveCount: number,
  currentMatchingQueryKey: string,
): number {
  switch (selection.mode) {
    case "ids":
      return selection.ids.size;
    case "matching_query":
      if (selection.queryKey !== currentMatchingQueryKey) return 0;
      return Math.max(0, matchingTotal - selection.excludedIds.size);
    case "all_active":
      return Math.max(0, orgActiveCount - selection.excludedIds.size);
    default: {
      const exhaustive: never = selection;
      return exhaustive;
    }
  }
}

export function contactsHeaderCheckboxState(
  selection: ContactsSelectionState,
  visibleIds: readonly number[],
  currentMatchingQueryKey: string,
): ContactsHeaderCheckboxState {
  if (visibleIds.length === 0) return "none";

  let selected = 0;
  for (const id of visibleIds) {
    if (isContactRowSelected(selection, id, currentMatchingQueryKey)) selected += 1;
  }
  if (selected === 0) return "none";
  return selected === visibleIds.length ? "all" : "some";
}

export function currentPageFullySelected(
  selection: ContactsSelectionState,
  visibleIds: readonly number[],
): boolean {
  if (selection.mode !== "ids" || visibleIds.length === 0) return false;
  return visibleIds.every((id) => selection.ids.has(id));
}

export function canOfferSelectMatching(
  selection: ContactsSelectionState,
  visibleCount: number,
  matchingTotal: number,
  visibleIds: readonly number[],
): boolean {
  if (matchingTotal <= visibleCount || visibleCount === 0) return false;
  return currentPageFullySelected(selection, visibleIds);
}

export function canOfferSelectAllActive(
  selection: ContactsSelectionState,
  matchingTotal: number,
  orgActiveCount: number,
  selectedCount: number,
): boolean {
  if (selection.mode === "all_active") return false;
  if (selectedCount <= 0 || orgActiveCount <= 0) return false;
  return orgActiveCount > matchingTotal;
}

export function toggleSemanticContactExclusion(
  selection: Extract<ContactsSelectionState, { mode: "matching_query" | "all_active" }>,
  contactId: number,
): ContactsSelectionState {
  const next = cloneExcludedIds(selection.excludedIds);
  if (next.has(contactId)) next.delete(contactId);
  else next.add(contactId);
  return withSemanticExclusions(selection, next);
}

export function excludeVisibleContactsFromSemanticSelection(
  selection: Extract<ContactsSelectionState, { mode: "matching_query" | "all_active" }>,
  visibleIds: readonly number[],
): ContactsSelectionState {
  const next = cloneExcludedIds(selection.excludedIds);
  for (const id of visibleIds) next.add(id);
  return withSemanticExclusions(selection, next);
}

export function includeVisibleContactsInSemanticSelection(
  selection: Extract<ContactsSelectionState, { mode: "matching_query" | "all_active" }>,
  visibleIds: readonly number[],
): ContactsSelectionState {
  const next = cloneExcludedIds(selection.excludedIds);
  for (const id of visibleIds) next.delete(id);
  return withSemanticExclusions(selection, next);
}

export function toggleContactsHeaderPageSelection(
  selection: ContactsSelectionState,
  visibleIds: readonly number[],
  currentMatchingQueryKey: string,
): ContactsSelectionState {
  const header = contactsHeaderCheckboxState(selection, visibleIds, currentMatchingQueryKey);
  if (selection.mode === "ids") {
    if (header === "all") return emptyContactsSelection();
    return { mode: "ids", ids: new Set(visibleIds) };
  }
  if (header === "all") {
    return excludeVisibleContactsFromSemanticSelection(selection, visibleIds);
  }
  return includeVisibleContactsInSemanticSelection(selection, visibleIds);
}

export function toggleExplicitContactSelection(
  ids: ReadonlySet<number>,
  contactId: number,
): ContactsSelectionState {
  const next = new Set(ids);
  if (next.has(contactId)) next.delete(contactId);
  else next.add(contactId);
  return { mode: "ids", ids: next };
}

export function syncContactsCheckboxIndeterminate(
  node: Pick<HTMLInputElement, "indeterminate"> | null,
  state: ContactsHeaderCheckboxState,
): void {
  if (node) node.indeterminate = state === "some";
}

export function toContactBulkSelectionPayload(
  selection: ContactsSelectionState,
  matchingQuery: ContactListQuery,
  currentMatchingQueryKey: string,
): ContactBulkSelection | null {
  switch (selection.mode) {
    case "ids":
      if (selection.ids.size === 0) return null;
      return { mode: "ids", ids: [...selection.ids] };
    case "matching_query":
      if (selection.queryKey !== currentMatchingQueryKey) return null;
      return {
        mode: "matching_query",
        query: toContactBulkMatchingQuery(matchingQuery),
        excludedIds: selection.excludedIds.size > 0 ? [...selection.excludedIds] : undefined,
      };
    case "all_active":
      return {
        mode: "all_active",
        excludedIds: selection.excludedIds.size > 0 ? [...selection.excludedIds] : undefined,
      };
    default: {
      const exhaustive: never = selection;
      return exhaustive;
    }
  }
}
